import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";
import {
  statutLabel, statutColor, typeAnimalLabel, causeLabel, fmtEuro, chipPastel,
} from "components/sacrificesConfig";
import CarteStat, { ombreLegere } from "components/SacrificesCarteStat";

const filtresVides = { statut: "", type_animal: "", cause: "", ville: "" };

// La ville est portée par l'en-tête de groupe, d'où son absence dans les colonnes.
// poids = largeur en % ; des = point de rupture à partir duquel la colonne s'affiche
// (masquée sur les écrans plus étroits, l'info reste dans la fiche détail)
const colonnes = [
  { cle: "directeur", label: "Directeur", poids: 10, des: "md" },
  { cle: "beneficiaire", label: "Bénéficiaire", poids: 15 },
  { cle: "saisi", label: "Saisi par", poids: 7, align: "center", des: "lg" },
  { cle: "telephone", label: "Téléphone", poids: 8, des: "xl" },
  { cle: "animal", label: "Animal", poids: 9, des: "md" },
  { cle: "nb", label: "Nb", poids: 5, align: "center", des: "sm" },
  { cle: "cause", label: "Cause", poids: 12, des: "md" },
  { cle: "total", label: "Total", poids: 9, align: "center", des: "sm" },
  { cle: "video", label: "Vidéo", poids: 5, align: "center", des: "lg" },
  { cle: "statut", label: "Statut", poids: 13 },
  { cle: "actions", label: "Actions", poids: 7, align: "center" },
];
const SANS_VILLE = "Sans ville";

// Tableau à largeur fixe = 100 % du conteneur : jamais de défilement horizontal.
// Mêmes largeurs dans l'en-tête et dans chaque ville pour garder les colonnes alignées.
const styleTableau = { width: "100%", tableLayout: "fixed" };
const styleCellule = (c) => ({
  width: c.poids + "%",
  px: 1.25,
  display: c.des ? { xs: "none", [c.des]: "table-cell" } : "table-cell",
});
const colonne = (cle) => colonnes.find((c) => c.cle === cle);
const ellipsis = { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };


const styleEnteteColonne = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#546e7a",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  whiteSpace: "nowrap",
};

const fmtDateHeure = (d) => (d
  ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })
  : "—");

// Section du dialog détail : bandeau titre coloré + lignes libellé/valeur
function SectionDetail({ icon, titre, color, lignes }) {
  return (
    <MDBox sx={{ border: "1px solid " + color + "33", borderRadius: "10px", overflow: "hidden", height: "100%" }}>
      <MDBox display="flex" alignItems="center" gap={1} px={2} py={1} sx={{ bgcolor: color + "14", color }}>
        <Icon fontSize="small">{icon}</Icon>
        <MDTypography variant="button" fontWeight="bold" sx={{ color }}>{titre}</MDTypography>
      </MDBox>
      <MDBox px={2} py={1}>
        {lignes.map(([label, valeur]) => (
          <MDBox
            key={label} display="flex" justifyContent="space-between" gap={2} py={0.75}
            sx={{ borderBottom: "1px dashed #eee", "&:last-child": { borderBottom: "none" } }}
          >
            <MDTypography variant="caption" color="text">{label}</MDTypography>
            <MDTypography variant="caption" fontWeight="bold" textAlign="right">{valeur}</MDTypography>
          </MDBox>
        ))}
      </MDBox>
    </MDBox>
  );
}

function SacrificesAdmin() {
  const [sacrifices, setSacrifices] = useState([]);
  const [villes, setVilles] = useState([]);
  const [stats, setStats] = useState(null);
  const [filtres, setFiltres] = useState(filtresVides);
  const [detail, setDetail] = useState(null);
  // Villes dépliées (toutes repliées par défaut)
  const [villesOuvertes, setVillesOuvertes] = useState({});

  const filtresActifs = Object.values(filtres).some((v) => v);

  // Regroupement des demandes par ville, triées alphabétiquement ("Sans ville" en dernier)
  const groupes = Object.values(
    sacrifices.reduce((acc, s) => {
      const ville = s.ville || SANS_VILLE;
      if (!acc[ville]) acc[ville] = { ville, demandes: [], total: 0, aAccepter: 0 };
      acc[ville].demandes.push(s);
      acc[ville].total += parseFloat(s.total_calcule) || 0;
      if (s.statut === "mandat_envoye") acc[ville].aAccepter += 1;
      return acc;
    }, {})
  ).sort((a, b) => {
    if (a.ville === SANS_VILLE) return 1;
    if (b.ville === SANS_VILLE) return -1;
    return a.ville.localeCompare(b.ville, "fr");
  });

  const toutesOuvertes = groupes.length > 0 && groupes.every((g) => villesOuvertes[g.ville]);
  const basculerVille = (ville) => setVillesOuvertes((o) => ({ ...o, [ville]: !o[ville] }));

  const fetchSacrifices = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filtres).filter(([, v]) => v));
      const res = await api.get("/sacrifices", { params });
      setSacrifices(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchReferentiels = async () => {
    try {
      const [tous, st] = await Promise.all([api.get("/sacrifices"), api.get("/sacrifices/stats")]);
      setVilles([...new Set(tous.data.map((s) => s.ville).filter(Boolean))].sort());
      setStats(st.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchReferentiels(); }, []);
  useEffect(() => { fetchSacrifices(); }, [filtres]);

  const accepterMandats = async (s) => {
    if (!window.confirm(`Accepter les mandats de ${s.beneficiaire_prenom} ${s.beneficiaire_nom} (${s.ville || "—"}) ?`)) return;
    try {
      await api.put("/sacrifices/" + s.id + "/statut", { statut: "mandats_acceptes" });
      setDetail(null);
      fetchSacrifices();
      fetchReferentiels();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur serveur");
    }
  };

  const selectFiltre = (champ, label, options) => (
    <Grid item xs={12} sm={6} md={2.6}>
      <FormControl size="small" fullWidth>
        <InputLabel id={"f-" + champ}>{label}</InputLabel>
        <Select
          labelId={"f-" + champ} label={label} sx={{ height: 40, bgcolor: "white" }}
          value={filtres[champ]}
          onChange={(e) => setFiltres({ ...filtres, [champ]: e.target.value })}
        >
          <MenuItem value="">Tous</MenuItem>
          {Object.entries(options).map(([v, l]) => (
            <MenuItem key={v} value={v}>{l}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );

  // Badge statut : couleur vive, forme pilule
  const badgeStatut = (statut) => (
    <Chip
      label={statutLabel[statut]} size="small"
      sx={{
        bgcolor: statutColor[statut], color: "white", fontWeight: "bold",
        borderRadius: "999px", maxWidth: "100%", px: 0.5,
      }}
    />
  );

  const cellule = (cle, contenu, props = {}) => {
    const c = colonne(cle);
    return (
      <TableCell align={c.align || "left"} {...props} sx={{ ...styleCellule(c), ...props.sx }}>
        {typeof contenu === "string" || typeof contenu === "number" ? (
          <MDTypography variant="caption" color="text" fontWeight="regular" title={String(contenu)} sx={ellipsis}>
            {contenu}
          </MDTypography>
        ) : contenu}
      </TableCell>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ boxShadow: ombreLegere }}>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "white" }}>volunteer_activism</Icon>
                  <MDTypography variant="h6" color="white">Toutes les demandes de sacrifices</MDTypography>
                </MDBox>
                <MDTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
                  {sacrifices.length} demande(s){filtresActifs ? " (filtrées)" : ""}
                </MDTypography>
              </MDBox>

              {/* Statistiques */}
              <MDBox pt={4} px={{ xs: 2, md: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} lg={3}>
                    <CarteStat icon="assignment" label="Demandes" valeur={stats ? stats.nb_demandes : "—"} color="#1976d2" />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <CarteStat icon="pets" label="Animaux" valeur={stats ? stats.nb_animaux : "—"} color="#7b1fa2" />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <CarteStat icon="euro" label="Montant total" valeur={stats ? fmtEuro(stats.total) : "—"} color="#43a047" />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <CarteStat icon="videocam" label="Option vidéo" valeur={stats ? stats.nb_option_video : "—"} color="#fb8c00" />
                  </Grid>
                </Grid>
              </MDBox>

              {/* Filtres */}
              <MDBox mx={{ xs: 2, md: 3 }} mt={3} p={2} sx={{ bgcolor: "#f8f9fa", borderRadius: "10px" }}>
                <Grid container spacing={2} alignItems="center">
                  {selectFiltre("statut", "Statut", statutLabel)}
                  {selectFiltre("type_animal", "Type d'animal", typeAnimalLabel)}
                  {selectFiltre("cause", "Cause", causeLabel)}
                  {selectFiltre("ville", "Ville", Object.fromEntries(villes.map((v) => [v, v])))}
                  <Grid item xs={12} md={1.6}>
                    <MDButton
                      variant="outlined" color="info" size="small" fullWidth
                      disabled={!filtresActifs} onClick={() => setFiltres(filtresVides)}
                      sx={{ height: 40 }}
                    >
                      <Icon>filter_alt_off</Icon>&nbsp;Réinitialiser
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Barre résumé */}
              <MDBox mx={{ xs: 2, md: 3 }} mt={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <MDTypography variant="button" fontWeight="regular" sx={{ color: "#78909c" }}>
                  {groupes.length} ville(s) · {sacrifices.length} demande(s)
                </MDTypography>
                {groupes.length > 0 && (
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      variant="outlined" color="info" size="small" disabled={toutesOuvertes}
                      onClick={() => setVillesOuvertes(Object.fromEntries(groupes.map((g) => [g.ville, true])))}
                    >
                      <Icon>unfold_more</Icon>&nbsp;Tout déplier
                    </MDButton>
                    <MDButton
                      variant="outlined" color="secondary" size="small"
                      disabled={!groupes.some((g) => villesOuvertes[g.ville])}
                      onClick={() => setVillesOuvertes({})}
                    >
                      <Icon>unfold_less</Icon>&nbsp;Tout replier
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>

              {/* Tableau groupé par ville : pleine largeur et pleine hauteur, sans défilement */}
              <MDBox mx={{ xs: 2, md: 3 }} mt={1.5} mb={3}>
                <MDBox pb={0.5}>
                  <Table sx={styleTableau}>
                    <MDBox component="thead" sx={{ bgcolor: "#f8f9fa" }}>
                      <TableRow>
                        {colonnes.map((c) => (
                          <TableCell key={c.cle} align={c.align || "left"} sx={{ ...styleCellule(c), py: 1.25, borderBottom: "none" }}>
                            <MDBox component="span" title={c.label} sx={{ ...styleEnteteColonne, ...ellipsis }}>{c.label}</MDBox>
                          </TableCell>
                        ))}
                      </TableRow>
                    </MDBox>
                  </Table>

                  {groupes.map((g) => {
                    const ouverte = !!villesOuvertes[g.ville];
                    return (
                      <MDBox key={g.ville} mt={1.25}>
                        <MDBox
                          onClick={() => basculerVille(g.ville)}
                          display="flex" alignItems="center" gap={1.5} flexWrap="wrap" px={2} py={1.5}
                          sx={{
                            cursor: "pointer",
                            bgcolor: "#e3f2fd",
                            borderLeft: "4px solid #1976d2",
                            borderRadius: "8px",
                            boxShadow: "0 1px 4px rgba(25, 118, 210, 0.12)",
                            transition: "background-color 0.2s, box-shadow 0.2s",
                            userSelect: "none",
                            "&:hover": { bgcolor: "#d6ebfc", boxShadow: "0 2px 8px rgba(25, 118, 210, 0.18)" },
                          }}
                        >
                          <Icon
                            sx={{
                              color: "#1976d2",
                              transition: "transform 0.25s ease",
                              transform: ouverte ? "rotate(90deg)" : "rotate(0deg)",
                            }}
                          >
                            chevron_right
                          </Icon>
                          <MDTypography sx={{ color: "#0d47a1", fontSize: "15px", fontWeight: "bold" }}>
                            {g.ville}
                          </MDTypography>
                          <Chip
                            size="small"
                            label={`${g.demandes.length} demande${g.demandes.length > 1 ? "s" : ""}`}
                            sx={{ bgcolor: "#1976d2", color: "white", fontWeight: "bold" }}
                          />
                          <Chip
                            size="small"
                            icon={<Icon sx={{ color: "white !important" }}>euro</Icon>}
                            label={fmtEuro(g.total)}
                            sx={{ bgcolor: "#43a047", color: "white", fontWeight: "bold" }}
                          />
                          {g.aAccepter > 0 && (
                            <Chip
                              size="small" sx={{ ...chipPastel(statutColor.mandat_envoye), ml: "auto" }}
                              label={`${g.aAccepter} mandat${g.aAccepter > 1 ? "s" : ""} à accepter`}
                            />
                          )}
                        </MDBox>

                        <Collapse in={ouverte} timeout="auto" unmountOnExit>
                          <Table sx={styleTableau}>
                            <TableBody>
                              {g.demandes.map((s) => (
                                <TableRow
                                  key={s.id}
                                  onClick={() => setDetail(s)}
                                  sx={{
                                    cursor: "pointer",
                                    bgcolor: "white",
                                    transition: "background-color 0.15s",
                                    "& td": { borderBottom: "1px solid #f0f0f0" },
                                    "&:hover": { bgcolor: "#f5f9ff" },
                                  }}
                                >
                                  {cellule("directeur", s.directeur_nom || "—")}
                                  {cellule(
                                    "beneficiaire",
                                    <MDTypography
                                      variant="button" fontWeight="bold" sx={ellipsis}
                                      title={`${s.beneficiaire_nom.toUpperCase()} ${s.beneficiaire_prenom}`}
                                    >
                                      {s.beneficiaire_nom.toUpperCase()} {s.beneficiaire_prenom}
                                    </MDTypography>
                                  )}
                                  {cellule(
                                    "saisi",
                                    s.saisi_par_id ? (
                                      <Tooltip title={s.saisi_par_nom || ""}>
                                        <Chip label="Délégué" size="small" sx={{ bgcolor: "#eceff1", color: "#546e7a", fontWeight: "bold", maxWidth: "100%" }} />
                                      </Tooltip>
                                    ) : ""
                                  )}
                                  {cellule("telephone", s.beneficiaire_telephone || "—")}
                                  {cellule("animal", typeAnimalLabel[s.type_animal])}
                                  {cellule("nb", s.nombre)}
                                  {cellule("cause", causeLabel[s.cause])}
                                  {cellule(
                                    "total",
                                    <MDTypography variant="button" fontWeight="bold" sx={ellipsis}>{fmtEuro(s.total_calcule)}</MDTypography>
                                  )}
                                  {cellule(
                                    "video",
                                    s.option_video ? (
                                      <Tooltip title="Option vidéo demandée">
                                        <Icon sx={{ color: "#fb8c00", verticalAlign: "middle" }}>videocam</Icon>
                                      </Tooltip>
                                    ) : ""
                                  )}
                                  {cellule("statut", badgeStatut(s.statut))}
                                  {cellule(
                                    "actions",
                                    s.statut === "mandat_envoye" ? (
                                      <Tooltip title="Accepter les mandats">
                                        <MDButton
                                          variant="text" color="success" size="small"
                                          onClick={() => accepterMandats(s)} sx={{ whiteSpace: "nowrap", px: 0.5, minWidth: 0 }}
                                        >
                                          <Icon>task_alt</Icon>
                                          <MDBox component="span" sx={{ display: { xs: "none", xl: "inline" } }}>&nbsp;Accepter</MDBox>
                                        </MDButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Voir le détail">
                                        <Icon sx={{ cursor: "pointer", color: "#90a4ae", "&:hover": { color: "#1976d2" } }} onClick={() => setDetail(s)}>
                                          visibility
                                        </Icon>
                                      </Tooltip>
                                    ),
                                    { onClick: (e) => e.stopPropagation() }
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </MDBox>
                    );
                  })}

                  {sacrifices.length === 0 && (
                    <MDBox py={5} textAlign="center">
                      <Icon sx={{ fontSize: "2.5rem !important", color: "#c4c9d4" }}>inbox</Icon>
                      <MDTypography variant="body2" color="text" display="block">
                        {filtresActifs ? "Aucune demande ne correspond aux filtres." : "Aucune demande pour le moment."}
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog détail */}
      <Dialog
        open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="md"
        PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
      >
        {detail && (
          <>
            <MDBox
              variant="gradient" bgColor="info" px={3} py={2}
              display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}
            >
              <MDBox>
                <MDTypography variant="h6" color="white">
                  {detail.beneficiaire_prenom} {detail.beneficiaire_nom.toUpperCase()}
                </MDTypography>
                <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                  Demande n°{detail.id} · {detail.session_libelle}
                </MDTypography>
              </MDBox>
              <Chip
                label={statutLabel[detail.statut]} size="small"
                sx={{ bgcolor: "white", color: statutColor[detail.statut], fontWeight: "bold" }}
              />
            </MDBox>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <SectionDetail
                    icon="person" titre="Bénéficiaire" color="#1976d2"
                    lignes={[
                      ["Nom", detail.beneficiaire_nom.toUpperCase()],
                      ["Prénom", detail.beneficiaire_prenom],
                      ["Téléphone", detail.beneficiaire_telephone || "—"],
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SectionDetail
                    icon="pets" titre="Sacrifice" color="#7b1fa2"
                    lignes={[
                      ["Type d'animal", typeAnimalLabel[detail.type_animal]],
                      ["Nombre", detail.nombre],
                      ["Cause", causeLabel[detail.cause]],
                      ["Option vidéo", detail.option_video ? "📹 Oui" : "Non"],
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SectionDetail
                    icon="location_city" titre="Rattachement" color="#43a047"
                    lignes={[
                      ["Ville", detail.ville || "—"],
                      ["Directeur", detail.directeur_nom || "—"],
                      ["Saisi par", detail.saisi_par_nom ? `${detail.saisi_par_nom} (délégué)` : "Directeur"],
                      ["Session", detail.session_libelle],
                    ]}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SectionDetail
                    icon="history" titre="Suivi" color="#fb8c00"
                    lignes={[
                      ["Statut", badgeStatut(detail.statut)],
                      ["Créée le", fmtDateHeure(detail.created_at)],
                      ["Mise à jour", fmtDateHeure(detail.updated_at)],
                    ]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDBox
                    display="flex" justifyContent="space-between" alignItems="center" px={2.5} py={1.5}
                    sx={{ bgcolor: "#43a04714", border: "1px solid #43a04733", borderRadius: "10px" }}
                  >
                    <MDTypography variant="button" color="text">Total calculé</MDTypography>
                    <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                      {fmtEuro(detail.total_calcule)}
                    </MDTypography>
                  </MDBox>
                </Grid>
                {detail.notes && (
                  <Grid item xs={12}>
                    <MDBox px={2.5} py={1.5} sx={{ bgcolor: "#f8f9fa", borderRadius: "10px", borderLeft: "4px solid #7b809a" }}>
                      <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={0.5}>
                        Notes
                      </MDTypography>
                      <MDTypography variant="button" fontWeight="regular" sx={{ whiteSpace: "pre-wrap" }}>
                        {detail.notes}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <MDButton onClick={() => setDetail(null)} color="secondary" variant="outlined">Fermer</MDButton>
              {detail.statut === "mandat_envoye" && (
                <MDButton color="success" onClick={() => accepterMandats(detail)}>
                  <Icon>task_alt</Icon>&nbsp;Accepter mandats
                </MDButton>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default SacrificesAdmin;
